import { useEffect, useState } from "react";
import { api } from "../api";
import { X, History, Pencil, Plus, Trash2, RotateCcw, CheckCircle2, ArrowRight, FileUp } from "lucide-react";

const ACTION_META = {
  create: { icon: Plus, color: "var(--mint)", label: "Created" },
  update: { icon: Pencil, color: "var(--warn)", label: "Edited" },
  status: { icon: ArrowRight, color: "var(--warn)", label: "Status changed" },
  delete: { icon: Trash2, color: "var(--danger)", label: "Deleted" },
  restore: { icon: RotateCcw, color: "var(--mint)", label: "Restored" },
  mark_released: { icon: CheckCircle2, color: "var(--mint)", label: "Marked as Released" },
};

// Field keys that store a single artist UUID → resolve via artistMap
const ARTIST_ID_FIELDS = new Set(["paid_for_artist_id", "linked_artist_id", "artist"]);
// Field keys that store a single release UUID → resolve via releaseMap
const RELEASE_ID_FIELDS = new Set(["paid_for_song_id", "linked_release", "linked_song_id", "related_release"]);
// Field keys that store an array of artist UUIDs
const MULTI_ARTIST_FIELDS = new Set(["artists", "lyricists", "mix_engineers"]);

// Human-friendly labels for raw field keys (fallback = title-cased key)
const FIELD_LABELS = {
  song: "Song Name",
  song_title: "Song Name",
  producer: "Producer Name",
  producer_email: "Email",
  soundcloud: "Audio Link",
  date: "Date",
  status: "Status",
  paid_to: "Paid To",
  paid_for_artist_id: "Paid For (Artist)",
  paid_for_song_id: "Paid For (Song)",
  linked_artist_id: "Linked Artist",
  linked_song_id: "Linked Song",
  linked_release: "Linked Release",
  related_release: "Related Release",
  artist: "Artist",
  artists: "Artist(s)",
  lyricists: "Lyricist(s)",
  mix_engineers: "Mix Engineer(s)",
  release_date: "Release Date",
  planned_date: "Planned Date",
  spotify: "Spotify",
  apple_music: "Apple Music",
  youtube_music: "YouTube Music",
  youtube_video: "YouTube Video",
  youtube: "YouTube",
  instagram: "Instagram",
  amount: "Amount (₹)",
  amount_inr: "Amount (₹)",
  received_after_usd: "Received (USD)",
  fees_usd: "Fees (USD)",
  tax_usd: "Tax (USD)",
  vendor: "Vendor",
  other_vendor_name: "Vendor (Other)",
  type: "Type",
  description: "Description",
  notes: "Notes",
  genre: "Genre",
  role: "Role",
  name: "Name",
  title: "Song Name",
  source: "Source",
  master_split_rows: "Master Split",
  publishing_split_rows: "Publishing Split",
  asset_calendar: "Asset Calendar",
  cover_art: "Cover Art",
};

const prettyField = (k) => {
  if (FIELD_LABELS[k]) return FIELD_LABELS[k];
  return String(k || "").replace(/_/g, " ").replace(/\b\w/g, m => m.toUpperCase());
};

export default function HistoryDrawer({ entryId, onClose }) {
  const [state, setState] = useState({ loading: true, entry: null, history: [] });
  const [artistMap, setArtistMap] = useState({});
  const [releaseMap, setReleaseMap] = useState({});

  useEffect(() => {
    api.history(entryId).then(d => setState({ loading: false, ...d })).catch(() => setState({ loading: false, entry: null, history: [] }));
  }, [entryId]);

  useEffect(() => {
    // Load resolver maps so UUIDs in change diffs render as human names.
    api.entries.list("artist_database").then(r => {
      const m = {};
      (r.entries || []).forEach(a => { m[a.id] = a.data?.name || a.id; });
      setArtistMap(m);
    }).catch(() => {});
    api.linkedReleases().then(list => {
      const m = {};
      (list || []).forEach(r => { m[r.id] = r.title + (r.artist ? ` — ${r.artist}` : ""); });
      setReleaseMap(m);
    }).catch(() => {});
  }, []);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000 }} data-testid="history-drawer">
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }} />
      <div style={{ position: "absolute", top: 0, right: 0, height: "100%", width: "min(520px, 92vw)", background: "var(--panel)", borderLeft: "1px solid var(--border)", padding: 24, overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <History size={16} style={{ color: "var(--mint)" }} />
            <h3 style={{ fontSize: 15, margin: 0 }}>Change history</h3>
          </div>
          <button className="btn" onClick={onClose} data-testid="close-history"><X size={14} /></button>
        </div>
        {state.loading ? <div className="muted">Loading…</div> :
         state.history.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, lineHeight: 1.6, padding: 20 }}>
            No history yet. Every future edit, status change or delete on this entry will show up here.
          </div>
        ) : (
          <div style={{ position: "relative", paddingLeft: 16 }}>
            <div style={{ position: "absolute", left: 4, top: 6, bottom: 6, width: 1, background: "var(--border)" }} />
            {state.history.map((h, i) => {
              const meta = ACTION_META[h.action] || { icon: History, color: "var(--muted)", label: h.action };
              const Icon = meta.icon;
              return (
                <div key={h.id} style={{ position: "relative", marginBottom: 18, paddingBottom: 14, borderBottom: i < state.history.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <div style={{ position: "absolute", left: -20, top: 2, width: 12, height: 12, borderRadius: "50%", background: meta.color, boxShadow: "0 0 0 3px var(--panel)" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <Icon size={13} style={{ color: meta.color }} />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{meta.label}</span>
                    <span className="muted" style={{ fontSize: 11 }}>by {h.actor_name || h.actor_email}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>{formatDate(h.at)}</div>
                  {h.note && <div style={{ fontSize: 12, color: "var(--mint)", marginBottom: 6 }}>{h.note}</div>}
                  {h.changes && h.changes.length > 0 && (
                    <div style={{ background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 8, padding: 10, fontSize: 12 }}>
                      {h.changes.map((c, j) => (
                        <div key={j} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 8, marginBottom: j < h.changes.length - 1 ? 6 : 0 }}>
                          <span style={{ color: "var(--muted)", textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em", paddingTop: 2 }}>{prettyField(c.field)}</span>
                          <span>
                            <span style={{ color: "var(--danger)", textDecoration: "line-through" }}>{fmt(c.from, c.field, artistMap, releaseMap)}</span>
                            <span className="muted" style={{ margin: "0 6px" }}>→</span>
                            <span style={{ color: "var(--mint)" }}>{fmt(c.to, c.field, artistMap, releaseMap)}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function fmt(v, fieldKey, artistMap = {}, releaseMap = {}) {
  if (v == null || v === "") return <span className="muted">—</span>;

  // Resolve UUIDs → human-readable names based on the field key.
  if (fieldKey && ARTIST_ID_FIELDS.has(fieldKey) && typeof v === "string") {
    return artistMap[v] || v;
  }
  if (fieldKey && RELEASE_ID_FIELDS.has(fieldKey) && typeof v === "string") {
    return releaseMap[v] || v;
  }
  if (fieldKey && MULTI_ARTIST_FIELDS.has(fieldKey) && Array.isArray(v)) {
    if (v.length === 0) return <span className="muted">—</span>;
    return v.map(id => artistMap[id] || id).join(", ");
  }

  if (Array.isArray(v)) return v.join(", ") || <span className="muted">—</span>;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
  const s = String(v);
  return s.length > 60 ? s.slice(0, 60) + "…" : s;
}
function formatDate(d) {
  if (!d) return "";
  try {
    // If the backend somehow returns a naive ISO string (no Z, no +HH:MM offset),
    // JS defaults to LOCAL time interpretation — which shows the wrong hour.
    // Force it to be treated as UTC by appending "Z" when a timezone marker is missing.
    const s = typeof d === "string" ? d : "";
    const needsZ = s && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
    const iso = needsZ ? s + "Z" : d;
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(d);
  }
}

export function ImportCsvButton({ dept, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const r = await api.importCsv(dept, f);
      setResult(r);
      onDone && onDone();
    } catch (err) {
      setResult({ error: String(err.message || err) });
    }
    e.target.value = "";
    setBusy(false);
  };
  return (
    <>
      <label className="btn clickable" style={{ padding: "8px 14px", fontSize: 13, cursor: busy ? "wait" : "pointer" }} data-testid={`import-csv-${dept}`}>
        <FileUp size={14} />
        {busy ? " Importing…" : " Import CSV"}
        <input type="file" accept=".csv,text/csv" onChange={onFile} disabled={busy} style={{ display: "none" }} />
      </label>
      {result && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1001, display: "grid", placeItems: "center", background: "rgba(0,0,0,0.55)", padding: 20 }} onClick={() => setResult(null)}>
          <div className="panel" style={{ padding: 24, maxWidth: 480, width: "100%" }} onClick={(e) => e.stopPropagation()}>
            {result.error ? (
              <>
                <h3 style={{ fontSize: 15, color: "var(--danger)" }}>Import failed</h3>
                <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>{result.error}</p>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: 15, marginBottom: 8 }}>Import complete</h3>
                <p style={{ fontSize: 13, marginBottom: 12 }}>
                  <span style={{ color: "var(--mint)", fontWeight: 600 }}>{result.created}</span> row{result.created !== 1 && "s"} imported
                  {result.errors?.length > 0 && <> · <span style={{ color: "var(--warn)" }}>{result.errors.length} error{result.errors.length !== 1 && "s"}</span></>}
                </p>
                {result.headers && (
                  <div className="muted" style={{ fontSize: 11, marginBottom: 10 }}>
                    Columns detected: {result.headers.join(", ")}
                  </div>
                )}
                {result.errors?.length > 0 && (
                  <div style={{ maxHeight: 140, overflowY: "auto", background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 8, padding: 10, fontSize: 12 }}>
                    {result.errors.slice(0, 10).map((er, i) => <div key={i} style={{ color: "var(--danger)" }}>Row {er.row}: {er.error}</div>)}
                  </div>
                )}
              </>
            )}
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={() => setResult(null)} data-testid="close-import-result">Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
